import axios from "axios";
import { getDefaultStore } from "jotai";
import { chatbotConfigState } from "../../state/config/ChatbotConfigState";
import { TRANSLATIONS } from "../constants/translations";
import { getBackendUrl, extractErrorMessage, buildScopedHeaders } from "./apiHelper";
import { getActiveChatbotId } from "./authUtils";

const BACKEND_URL = getBackendUrl();

function isValidUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || '').trim()
  );
}

function sanitizeChatbotId(value = null) {
  return isValidUuid(value) ? String(value).trim() : null;
}

function resolveChatbotId(userAuthToken = null, chatbotId = null, tenantId = null) {
  return sanitizeChatbotId(
    chatbotId || getActiveChatbotId({
      token: userAuthToken,
      tenantId,
    })
  );
}

function getActiveTranslation() {
  try {
    const store = getDefaultStore();
    const cfg = store.get(chatbotConfigState);
    const lang = (cfg?.language || "de").toLowerCase().startsWith("en") ? "en" : "de";
    return TRANSLATIONS[lang] || TRANSLATIONS.de;
  } catch (e) {
    return TRANSLATIONS.de;
  }
}

export async function processAndUploadDocument(formData, userAuthToken, chatbotId = null, tenantId = null) {
  const t = getActiveTranslation();
  try {
    const response = await axios.post(`${BACKEND_URL}/rag/upload`, formData, {
      headers: buildScopedHeaders({
        token: userAuthToken,
        chatbotId: resolveChatbotId(userAuthToken, chatbotId, tenantId),
        tenantId,
      }),
    });

    return {
      success: true,
      message: response.data?.message || t.successUpload,
      documentId: response.data?.documentId || null,
    };
  } catch (error) {
    console.error("RAG upload error:", extractErrorMessage(error));
    return {
      success: false,
      message: extractErrorMessage(error, t.kbUploadFailed),
    };
  }
}

export async function listKnowledgeDocuments(userAuthToken, chatbotId = null, tenantId = null) {
  const t = getActiveTranslation();
  try {
    const response = await axios.get(`${BACKEND_URL}/rag/documents`, {
      headers: buildScopedHeaders({
        token: userAuthToken,
        chatbotId: resolveChatbotId(userAuthToken, chatbotId, tenantId),
        tenantId,
      }),
    });

    return Array.isArray(response.data?.documents) ? response.data.documents : [];
  } catch (error) {
    console.error('RAG documents list error:', extractErrorMessage(error));
    throw new Error(extractErrorMessage(error, t.kbUploadFailed));
  }
}

// ADDED: Delete API Call
export async function deleteKnowledgeDocument(documentId, userAuthToken, chatbotId = null, tenantId = null) {
  try {
    const response = await axios.delete(`${BACKEND_URL}/rag/documents/${documentId}`, {
      headers: buildScopedHeaders({
        token: userAuthToken,
        chatbotId: resolveChatbotId(userAuthToken, chatbotId, tenantId),
        tenantId,
      }),
    });
    return { success: true };
  } catch (error) {
    console.error('RAG document delete error:', extractErrorMessage(error));
    return { success: false, message: extractErrorMessage(error, "Failed to delete document") };
  }
}